import { isNot } from '@ipfabric/utils/fp/comparison';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphToolbarDropdown } from 'components/Graph/GraphToolbar/GraphToolbarDropdown';
import { selectGraphToolbarDisabled } from 'components/Graph/GraphToolbar/selectors/selectGraphToolbarDisabled';
import { hideNotSelectedNodes } from 'components/Graph/commands/hideNotSelectedNodes';
import { hideSelectedNodes } from 'components/Graph/commands/hideSelectedNodes';
import { setPeekingAtHiddenNodes } from 'components/Graph/commands/setPeekingAtHiddenNodes';
import { showAllNodes } from 'components/Graph/commands/showAllNodes';
import { unhideSelectedNodes } from 'components/Graph/commands/unhideSelectedNodes';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { selectSelectedHiddenNodeIds } from 'components/Graph/model/selectors/selectSelectedHiddenNodeIds';
import { selectSelectedVisibleNodeIds } from 'components/Graph/model/selectors/selectSelectedVisibleNodeIds';
import { EyeIcon } from 'components/icons/EyeIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { flow } from 'fp-ts/lib/function';
import { memo } from 'react';
import type { FC } from 'react';

const BareGraphToolbarHiddenNodesMenu: FC = () => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    selectedVisibleNodeIds: selectSelectedVisibleNodeIds,
    toolbarDisabled: selectGraphToolbarDisabled,
    hasHiddenNodes: flow(prop('graph', 'hiddenNodes', 'size'), isNot(0)),
    pathLookupApplied: selectPathLookupApplied,
    peekingAtHiddenNodes: prop('peekingAtHiddenNodes'),
    selectedHiddenNodeIds: selectSelectedHiddenNodeIds,
  });

  const getTitle = () => {
    const unhideMessageKey =
      ctxProps.selectedVisibleNodeIds.size === 0
        ? 'unhideNodes'
        : 'hideOrUnhideNodes';

    return msg.canvasGraph.graphToolbar.buttons[
      ctxProps.hasHiddenNodes ? unhideMessageKey : 'hideNodes'
    ];
  };

  const getDisabledTitle = () => {
    if (ctxProps.toolbarDisabled) {
      return msg.canvasGraph.graphToolbar.buttons.selectDataToDisplay;
    }
    return ctxProps.pathLookupApplied
      ? msg.canvasGraph.graphToolbar.buttons.notAvailablePathlookup
      : msg.canvasGraph.graphToolbar.buttons.hideDisabled;
  };

  const disabled = ctxProps.toolbarDisabled || ctxProps.pathLookupApplied;

  return (
    <GraphToolbarDropdown
      title={getTitle()}
      disabledTitle={getDisabledTitle()}
      options={[
        {
          disabled: disabled || ctxProps.selectedVisibleNodeIds.size === 0,
          label: msg.canvasGraph.graphToolbar.buttons.hideSelected,
          onClick: () => {
            if (!disabled) {
              ctxProps.dispatch(hideSelectedNodes);
            }
          },
        },
        {
          disabled: disabled || ctxProps.selectedVisibleNodeIds.size === 0,
          label: msg.canvasGraph.graphToolbar.buttons.hideNotSelected,
          onClick: () => {
            if (!disabled) {
              ctxProps.dispatch(hideNotSelectedNodes);
            }
          },
        },
        {
          disabled: disabled || !ctxProps.hasHiddenNodes,
          label: msg.canvasGraph.graphToolbar.buttons.unhideToggle,
          onClick: () => {
            if (!disabled) {
              ctxProps.dispatch(setPeekingAtHiddenNodes, {
                peeking: !ctxProps.peekingAtHiddenNodes,
              });
            }
          },
          toggled: !disabled && ctxProps.peekingAtHiddenNodes,
        },
        {
          disabled:
            disabled ||
            !ctxProps.peekingAtHiddenNodes ||
            ctxProps.selectedHiddenNodeIds.size === 0,
          label: msg.canvasGraph.graphToolbar.buttons.unhideSelected,
          onClick: () => {
            if (!ctxProps.toolbarDisabled) {
              ctxProps.dispatch(unhideSelectedNodes);
            }
          },
        },
        {
          disabled: disabled || !ctxProps.hasHiddenNodes,
          label: msg.canvasGraph.graphToolbar.buttons.unhideAll,
          onClick: () => {
            if (!disabled) {
              ctxProps.dispatch(showAllNodes);
            }
          },
        },
      ]}
    >
      <EyeIcon title='Manage hidden nodes' width={16} height={10} />
    </GraphToolbarDropdown>
  );
};

export const GraphToolbarHiddenNodesMenu = memo(
  BareGraphToolbarHiddenNodesMenu,
);
